import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../../../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiCheck, FiX, FiClock, FiAlertCircle, FiFile, FiDownload, FiPlus, FiRotateCcw } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import TableComp, { Column } from '../../../../components/Table';
import {
    fetchCompanyIdAndPsoInfo,
    fetchBranches,
    fetchManagedPsos,
    fetchRequests,
    createRequest,
    updateRequestStatus,
    forwardRequestToHead,
    fetchAvailableCompanies,
    type Request
} from '../service/requestManagementService';

export default function RequestForm() {
    const pso = useAppSelector((s) => s.user.pso);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [psoId, setPsoId] = useState<string | null>(null);
    const [psoDesignation, setPsoDesignation] = useState<string | null>(null);
    const [managedPsoIds, setManagedPsoIds] = useState<string[]>([]);

    // Check if user can create requests
    // Regular PSO can create requests, but for incharge users, only Incharge MIS can create
    const designation = pso?.designation || psoDesignation || '';
    const isIncharge = /incharge/i.test(designation);
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isInchargeMIS = inchargeType.includes('mis');
    const isInchargeCCTV = inchargeType.includes('cctv');
    const isInchargeGuard = inchargeType.includes('guarding') || inchargeType.includes('guard');
    const isInchargeBAMS = inchargeType.includes('bams');
    const canCreateRequests = !isIncharge || isInchargeMIS; // Regular PSO or Incharge MIS can create

    // Determine automatic request type filter for incharge users
    // PSO and Incharge MIS see all requests, other incharge types see only their type
    const getAutoRequestTypeFilter = (): 'All' | 'CCTV' | 'Guard' | 'BAMS' => {
        if (!designation || !isIncharge || isInchargeMIS) {
            return 'All'; // PSO and Incharge MIS see all, or if designation not loaded yet
        }
        if (isInchargeCCTV) {
            return 'CCTV'; // Incharge CCTV sees only CCTV requests
        }
        if (isInchargeGuard) {
            return 'Guard'; // Incharge Guard sees only Guard requests
        }
        if (isInchargeBAMS) {
            return 'BAMS'; // Incharge BAMS sees only BAMS requests
        }
        return 'All'; // Default
    };
    const [requests, setRequests] = useState<Request[]>([]);
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [dateFromFilter, setDateFromFilter] = useState<string>('');
    const [dateToFilter, setDateToFilter] = useState<string>('');
    // Default filter status: 'pending' for Regional PSO, 'All' for others
    const [filterStatus, setFilterStatus] = useState('All');
    const [requestTypeFilter, setRequestTypeFilter] = useState<'All' | 'CCTV' | 'Guard' | 'BAMS'>('All');
    const [statuses, setStatuses] = useState<string[]>([]);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Add Request Form states
    const [showAddModal, setShowAddModal] = useState(false);
    const [psoName, setPsoName] = useState('');
    const [psoEmail, setPsoEmail] = useState('');
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
    const [branchSearch, setBranchSearch] = useState('');
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
    const [companySearch, setCompanySearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [branchDetailsCache, setBranchDetailsCache] = useState<Record<string, any>>({});
    const [locationDetailsCache, setLocationDetailsCache] = useState<Record<string, any>>({});
    const [displayBranchName, setDisplayBranchName] = useState<string>('N/A');
    const [displayRegionName, setDisplayRegionName] = useState<string>('N/A');

    // Form fields
    const [formData, setFormData] = useState({
        requestType: '',
        branchCode: '',
        branchName: '',
        company: '',
        selectedCompanyId: '',
        requesterName: '',
        requesterContact: '',
        requiredBy: '',
        cctvDateFrom: '',
        cctvDateTo: '',
        timeFrom: '',
        timeTo: '',
        numberOfGuards: '',
        guardCompany: '',
        hardwareName: '',
        purpose: '',
        purposeOther: '',
        requestId: '',
    });

    const requiredByOptions = ['HR', 'Admin', 'Police', 'FIA', 'FRMU', 'DRU', 'CIU', 'CIO', 'Region/Branch', 'Audit', 'Others'];
    // Show all request types for Incharge MIS, otherwise show only CCTV and Guard
    const requestTypeOptions = isInchargeMIS ? ['CCTV', 'Guard', 'BAMS'] : ['CCTV', 'Guard'];
    const purposeOptionsCCTV = ['ATM transection Issue', 'HR Enquiry', 'Audit observation', 'Police Investigation', 'Other'];
    const purposeOptionsGuard = ['BAMS Failure', 'Electrical Issue', 'Renovation Work', 'BISP Camp', 'Shutter Out of order', 'Other'];

    const uid = auth.currentUser?.uid;

    // Step 1: Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
        const isBankUser = userRole === 'bank';

        const loadCompanyInfo = async () => {
            try {
                if (isBankUser) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const directBank = await getDoc(doc(db, "banks", uid));
                    if (directBank.exists()) {
                        setCompanyId(uid);
                        return;
                    }
                    // Try query by userId field
                    const banksSnap = await getDocs(collection(db, "banks"));
                    for (const bankDoc of banksSnap.docs) {
                        const bankData = bankDoc.data();
                        if (bankData.userId === uid) {
                            setCompanyId(bankDoc.id);
                            return;
                        }
                    }
                } else {
                    // PSO or Incharge users
                    const companyInfo = await fetchCompanyIdAndPsoInfo(uid);
                    if (companyInfo) {
                        setCompanyId(companyInfo.companyId);
                        setPsoId(companyInfo.psoId);
                        setPsoDesignation(companyInfo.psoDesignation);
                        setPsoName(companyInfo.psoName);
                        setPsoEmail(companyInfo.psoEmail);
                    }
                }
            } catch (err) {
                console.error('Error fetching company info:', err);
            }
        };

        loadCompanyInfo();
    }, [uid]);

    // Fetch branches for add request modal
    // For Regional PSO and Head PSO, fetch all branches since they can see requests from other PSOs
    useEffect(() => {
        if (!companyId || !uid || !psoId || !psoDesignation) return;

        const loadBranches = async () => {
            try {
                // For Regional PSO and Head PSO, we need all branches to display branch names for requests from other PSOs
                // For regular PSO, we only need their own branches
                if (psoDesignation === 'Regional PSO' || psoDesignation === 'Head PSO') {
                    // Fetch all branches for the company
                    const branchSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
                    const allBranches = branchSnap.docs.map(doc => ({
                        id: doc.id,
                        companyId,
                        ...doc.data(),
                    }));
                    setBranches(allBranches);
                } else {
                    // Regular PSO - only fetch their own branches
                    const branchesList = await fetchBranches(companyId, psoId);
                    setBranches(branchesList);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };

        loadBranches();
    }, [companyId, uid, psoId, psoDesignation]);

    // Step 2: Fetch managed PSOs (if Regional PSO or Head PSO)
    useEffect(() => {
        if (!companyId || !psoId || !psoDesignation || !uid) return;

        const loadManagedPsos = async () => {
            try {
                const managedIds = await fetchManagedPsos(companyId, psoId, psoDesignation, uid);
                setManagedPsoIds(managedIds);
            } catch (error) {
                console.error('Error fetching managed PSOs:', error);
                setManagedPsoIds([]);
            }
        };

        loadManagedPsos();
    }, [companyId, psoId, psoDesignation, uid]);

    // Set default filter status to 'pending' for Regional PSO
    useEffect(() => {
        if (psoDesignation === 'Regional PSO') {
            setFilterStatus('pending');
        }
    }, [psoDesignation]);

    // Step 3: Fetch requests
    useEffect(() => {
        if (!companyId) return;

        const loadRequests = async () => {
            setLoading(true);
            try {
                const requestsList = await fetchRequests(companyId, psoId || '', psoDesignation || '', managedPsoIds);
                setRequests(requestsList);

                // Extract unique statuses
                const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to fetch requests');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadRequests();
    }, [companyId, psoId, managedPsoIds, psoDesignation]);

    // Get date fields based on request type
    const getDateFields = (request: Request) => {
        const requestType = request.requestType?.toLowerCase();

        if (requestType === 'guard') {
            return {
                dateFrom: request.guardDateFrom || request.cctvDateFrom || request.dateFrom,
                dateTo: request.guardDateTo || request.cctvDateTo || request.dateTo
            };
        } else if (requestType === 'bams') {
            return {
                dateFrom: request.bamsDateFrom || request.cctvDateFrom || request.dateFrom,
                dateTo: request.bamsDateTo || request.cctvDateTo || request.dateTo
            };
        } else {
            // CCTV or default
            return {
                dateFrom: request.cctvDateFrom || request.dateFrom,
                dateTo: request.cctvDateTo || request.dateTo
            };
        }
    };

    // Filter requests
    const filteredRequests = requests.filter(request => {
        const requestId = request.requestId || '';
        const branchCode = request.branchCode || '';
        const requesterName = request.requesterName || '';

        const matchesSearch = !searchTerm ||
            requestId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            branchCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
            requesterName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'All' || request.status === filterStatus;

        // For incharge users (except MIS), automatically filter by their type
        // For PSO and Incharge MIS, use the selected filter
        const autoFilter = getAutoRequestTypeFilter();
        const effectiveFilter = (isIncharge && !isInchargeMIS && !!designation) ? autoFilter : requestTypeFilter;

        // Case-insensitive comparison for request type
        const requestType = (request.requestType || '').trim();
        const matchesRequestType = effectiveFilter === 'All' ||
            requestType.toLowerCase() === effectiveFilter.toLowerCase();

        // Apply date filter
        let matchesDate = true;

        // Check date range filters (dateFromFilter and dateToFilter) - filter by created date
        if (dateFromFilter || dateToFilter) {
            if (!request.createdAt) {
                matchesDate = false;
            } else {
                // Convert filter dates to Date objects
                const filterFrom = dateFromFilter ? new Date(dateFromFilter) : null;
                const filterTo = dateToFilter ? new Date(dateToFilter) : null;

                // Convert request created date to Date object
                let createdDate: Date;
                if (request.createdAt.toDate) {
                    createdDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    createdDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    createdDate = new Date(request.createdAt);
                }

                // Set to start of day for comparison
                const createdDateOnly = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());

                if (filterFrom && filterTo) {
                    // Both filter dates provided - check if created date is within range
                    const filterFromDate = new Date(filterFrom.getFullYear(), filterFrom.getMonth(), filterFrom.getDate());
                    const filterToDate = new Date(filterTo.getFullYear(), filterTo.getMonth(), filterTo.getDate(), 23, 59, 59);
                    matchesDate = createdDateOnly >= filterFromDate && createdDateOnly <= filterToDate;
                } else if (filterFrom) {
                    // Only from date provided - check if created date is on or after filter date
                    const filterFromDate = new Date(filterFrom.getFullYear(), filterFrom.getMonth(), filterFrom.getDate());
                    matchesDate = createdDateOnly >= filterFromDate;
                } else if (filterTo) {
                    // Only to date provided - check if created date is on or before filter date
                    const filterToDate = new Date(filterTo.getFullYear(), filterTo.getMonth(), filterTo.getDate(), 23, 59, 59);
                    matchesDate = createdDateOnly <= filterToDate;
                }
            }
        } else if (dateFilter !== 'all') {
            // Use period filter (daily, weekly, monthly) if date range filters are not set
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            if (!request.createdAt) {
                matchesDate = false;
            } else {
                let requestDate: Date;
                if (request.createdAt.toDate) {
                    requestDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    requestDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    requestDate = new Date(request.createdAt);
                }

                const requestDateOnly = new Date(requestDate.getFullYear(), requestDate.getMonth(), requestDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        matchesDate = requestDateOnly.getTime() === today.getTime();
                        break;
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        matchesDate = requestDateOnly.getTime() >= weekAgo.getTime();
                        break;
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        matchesDate = requestDateOnly.getTime() >= monthAgo.getTime();
                        break;
                    default:
                        matchesDate = true;
                }
            }
        }

        return matchesSearch && matchesStatus && matchesRequestType && matchesDate;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus, requestTypeFilter, dateFilter, dateFromFilter, dateToFilter, itemsPerPage]);

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setDateFilter('all');
        setDateFromFilter('');
        setDateToFilter('');
        // Reset status filter based on user role
        if (psoDesignation === 'Regional PSO') {
            setFilterStatus('pending');
        } else {
            setFilterStatus('All');
        }
        setRequestTypeFilter('All');
        setCurrentPage(1);
    };

    // Fetch location details by locationId
    const fetchLocationDetails = async (locationId: string): Promise<any | null> => {
        // Check cache first
        if (locationDetailsCache[locationId]) {
            return locationDetailsCache[locationId];
        }

        try {
            const locationDoc = await getDoc(doc(db, 'locations', locationId));
            if (locationDoc.exists()) {
                const locationData = { id: locationDoc.id, ...locationDoc.data() };
                // Cache the location data
                setLocationDetailsCache(prev => ({ ...prev, [locationId]: locationData }));
                return locationData;
            }
        } catch (error) {
            console.error('Error fetching location details:', error);
        }
        return null;
    };

    // Fetch branch details by branchId
    const fetchBranchDetails = async (branchId: string, bankId: string): Promise<any | null> => {
        // Check cache first
        if (branchDetailsCache[branchId]) {
            return branchDetailsCache[branchId];
        }

        try {
            const branchDoc = await getDoc(doc(db, 'banks', bankId, 'branch', branchId));
            if (branchDoc.exists()) {
                const branchData: any = { id: branchDoc.id, ...branchDoc.data() };

                // If branch has locationId, fetch location details
                if (branchData.locationId) {
                    const locationData = await fetchLocationDetails(branchData.locationId);
                    if (locationData) {
                        // Store location name in branch data for easy access
                        branchData.locationName = locationData.name;
                    }
                }

                // Cache the branch data (with locationName if fetched)
                setBranchDetailsCache(prev => ({ ...prev, [branchId]: branchData }));

                return branchData;
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
        return null;
    };

    // Get branch name from branches list or fetch if needed
    const getBranchName = async (request: Request): Promise<string> => {
        // First check if branchName is already in the request and is valid
        if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
            return request.branchName;
        }

        // Try to find branch from branches list by branchCode
        if (request.branchCode) {
            const branch = branches.find(b => b.branchCode === request.branchCode);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Try to find by branchId from branches list
        if (request.branchId) {
            const branch = branches.find(b => b.id === request.branchId);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // If branchId exists and we have companyId, try to fetch branch details
        if (request.branchId && companyId) {
            const branchData = await fetchBranchDetails(request.branchId, companyId);
            if (branchData) {
                return branchData.address || branchData.branchName || branchData.name || 'N/A';
            }
        }

        return 'N/A';
    };

    // Synchronous version for immediate display (will be updated when async fetch completes)
    const getBranchNameSync = (request: Request): string => {
        // First check if branchName is already in the request and is valid
        if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
            return request.branchName;
        }

        // Try to find branch from branches list by branchCode
        if (request.branchCode) {
            const branch = branches.find(b => b.branchCode === request.branchCode);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Try to find by branchId from branches list
        if (request.branchId) {
            const branch = branches.find(b => b.id === request.branchId);
            if (branch) {
                return branch.branchName || branch.address || branch.name || 'N/A';
            }
        }

        // Check cache
        if (request.branchId && branchDetailsCache[request.branchId]) {
            const branchData = branchDetailsCache[request.branchId];
            return branchData.address || branchData.branchName || branchData.name || 'N/A';
        }

        return 'N/A';
    };

    // Open details modal
    const handleViewDetails = async (request: Request) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);

        // Get initial branch name synchronously
        const initialBranchName = getBranchNameSync(request);
        setDisplayBranchName(initialBranchName);
        setDisplayRegionName('N/A');

        // Use bankId from request if available, otherwise use companyId
        const bankId = (request as any).bankId || companyId;

        // Try to get branch data and location
        let branchData: any = null;

        if (request.branchId) {
            try {
                // Check if branch is already in our list
                const existingBranch = branches.find(b => b.id === request.branchId);
                if (existingBranch) {
                    branchData = existingBranch;
                } else if (branchDetailsCache[request.branchId]) {
                    // Use cached branch data
                    branchData = branchDetailsCache[request.branchId];
                } else if (bankId) {
                    // Fetch branch details
                    branchData = await fetchBranchDetails(request.branchId, bankId);
                }

                // Update branch name if we have branch data
                if (branchData) {
                    const branchName = branchData.address || branchData.branchName || branchData.name || 'N/A';
                    setDisplayBranchName(branchName);

                    // Get location/region name
                    if (branchData.locationName) {
                        // Location name already in branch data
                        setDisplayRegionName(branchData.locationName);
                    } else if (branchData.locationId) {
                        // Fetch location details
                        const locationData = await fetchLocationDetails(branchData.locationId);
                        if (locationData && locationData.name) {
                            setDisplayRegionName(locationData.name);
                            // Update branch data cache with location name
                            if (branchDetailsCache[request.branchId]) {
                                branchDetailsCache[request.branchId].locationName = locationData.name;
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching branch/location data:', error);
            }
        }
    };

    // Update request status
    const handleUpdateStatus = async (request: Request, newStatus: string) => {
        if (!companyId || !uid || !psoDesignation) return;

        setUpdating(true);
        try {
            await updateRequestStatus(companyId, request.id, newStatus, uid);

            setToastMessage(`Request status updated to ${newStatus}!`);
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, psoDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);

            setShowDetailsModal(false);
            setSelectedRequest(null);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to update request status');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    // Forward request to Head PSO
    const handleForwardToHead = async (request: Request) => {
        if (!companyId || !uid || !psoDesignation) return;

        setUpdating(true);
        try {
            await forwardRequestToHead(companyId, request.id, uid);

            setToastMessage('Request forwarded to Head PSO!');
            setToastType('success');
            setShowToast(true);

            // Refresh requests list
            const requestsList = await fetchRequests(companyId, psoId || uid, psoDesignation, managedPsoIds);
            setRequests(requestsList);
            const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
            setStatuses(statusList);

            setShowDetailsModal(false);
            setSelectedRequest(null);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to forward request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUpdating(false);
        }
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        } else if (statusLower === 'approved') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'rejected') {
            bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
        } else if (statusLower === 'in_progress') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (statusLower === 'completed') {
            bgColor = 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
        } else if (statusLower === 'forwarded_to_head') {
            bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status === 'forwarded_to_head' ? 'Forwarded To Head' : status}
            </span>
        );
    };

    // Get request type badge
    const getRequestTypeBadge = (type?: string) => {
        if (!type) return null;

        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (type === 'CCTV') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (type === 'Guard') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (type === 'BAMS') {
            bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {type}
            </span>
        );
    };

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Format datetime
    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleString();
        }
        return new Date(date).toLocaleString();
    };

    // Generate Request ID
    const generateRequestId = () => {
        if (!psoName || psoName.length < 2) {
            const timestamp = Date.now().toString().slice(-4);
            return `REQ-XX-${timestamp}`;
        }
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `REQ-${psoInitials}-${timestamp}`;
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            requestType: '',
            branchCode: '',
            branchName: '',
            company: '',
            selectedCompanyId: '',
            requesterName: '',
            requesterContact: '',
            requiredBy: '',
            cctvDateFrom: '',
            cctvDateTo: '',
            timeFrom: '',
            timeTo: '',
            numberOfGuards: '',
            guardCompany: '',
            hardwareName: '',
            purpose: '',
            purposeOther: '',
            requestId: '',
        });
        setSelectedBranch(null);
        setBranchSearch('');
    };

    // Filtered branches for search
    const filteredBranches = branches.filter(
        (b) =>
            b.branchCode?.toLowerCase().includes(branchSearch.toLowerCase()) ||
            b.address?.toLowerCase().includes(branchSearch.toLowerCase())
    );

    // Fetch available companies based on request type
    const loadAvailableCompanies = async () => {
        if (!companyId || !formData.requestType) return;

        try {
            const companiesList = await fetchAvailableCompanies(companyId, formData.requestType);
            setAvailableCompanies(companiesList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    // Handle branch selection
    const handleBranchSelection = (branch: any) => {
        setSelectedBranch(branch);
        setFormData({
            ...formData,
            branchCode: branch.branchCode,
            branchName: branch.branchName || branch.address,
        });
        setShowBranchModal(false);
    };

    // Handle company selection
    const handleCompanySelection = (company: any) => {
        setFormData({
            ...formData,
            company: company.companyName,
            selectedCompanyId: company.companyId,
        });
        setShowCompanyModal(false);
    };

    // Handle save request
    const handleSaveRequest = async () => {
        if (!uid || !selectedBranch) {
            setToastMessage('Please select a branch');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.cctvDateFrom || !formData.cctvDateTo) {
            setToastMessage('Please fill in all required fields');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Requester Name and Contact validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && (!formData.requesterName.trim() || !formData.requesterContact.trim())) {
            setToastMessage('Please fill in requester name and contact');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Required By validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && !formData.requiredBy) {
            setToastMessage('Please select Required By');
            setToastType('error');
            setShowToast(true);
            return;
        }

        // Time validation - only for non-Guard requests
        if (formData.requestType !== 'Guard' && (!formData.timeFrom || !formData.timeTo)) {
            setToastMessage('Please fill in time range');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (formData.requestType === 'Guard' && !formData.numberOfGuards) {
            setToastMessage('Please fill in number of guards');
            setToastType('error');
            setShowToast(true);
            return;
        }


        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setSaving(true);
        try {
            const newRequestId = generateRequestId();

            const requestData: any = {
                requestId: newRequestId,
                branchCode: formData.branchCode,
                branchName: formData.branchName,
                company: formData.company,
                companyId: formData.selectedCompanyId,
                requesterName: formData.requesterName.trim(),
                requesterContact: formData.requesterContact.trim(),
                requiredBy: formData.requiredBy,
                requestType: formData.requestType,
                cctvDateFrom: formData.cctvDateFrom,
                cctvDateTo: formData.cctvDateTo,
                timeFrom: formData.timeFrom,
                timeTo: formData.timeTo,
                purpose: formData.purpose,
                purposeOther: formData.purpose === 'Other' ? formData.purposeOther.trim() : '',
                branchId: selectedBranch.id,
                createdBy: psoId || uid,
                status: 'pending',
                createdAt: new Date(),
                hardwareName: formData.hardwareName,
            };

            // Add type-specific fields
            if (formData.requestType === 'Guard') {
                requestData.numberOfGuards = formData.numberOfGuards;
                requestData.guardCompany = formData.guardCompany;
            }

            await createRequest(companyId, requestData);

            setToastMessage(`${formData.requestType} Request submitted successfully! Request ID: ${newRequestId}`);
            setToastType('success');
            setShowToast(true);

            // Reset form and close modal
            resetForm();
            setShowAddModal(false);

            // Refresh requests list
            if (companyId && psoId && psoDesignation) {
                const requestsList = await fetchRequests(companyId, psoId, psoDesignation, managedPsoIds);
                setRequests(requestsList);
                const statusList = Array.from(new Set(requestsList.map(request => request.status).filter((status): status is string => Boolean(status))));
                setStatuses(statusList);
            }
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to submit request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setSaving(false);
        }
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        // Helper function to get branch data for a request
        const getBranchData = async (request: Request): Promise<any> => {
            let branchData: any = null;
            const bankId = (request as any).bankId || companyId;

            if (request.branchId) {
                // Check if branch is in our list
                const existingBranch = branches.find(b => b.id === request.branchId);
                if (existingBranch) {
                    branchData = existingBranch;
                } else if (branchDetailsCache[request.branchId]) {
                    branchData = branchDetailsCache[request.branchId];
                } else if (bankId) {
                    branchData = await fetchBranchDetails(request.branchId, bankId);
                }
            }
            return branchData;
        };

        // Helper function to get branch name for a request
        const getBranchName = async (request: Request): Promise<string> => {
            // First check if branchName is already in the request and is valid
            if (request.branchName && request.branchName.trim() !== '' && request.branchName !== 'N/A') {
                return request.branchName;
            }

            const branchData = await getBranchData(request);
            if (branchData) {
                return branchData.address || branchData.branchName || branchData.name || 'N/A';
            }

            return 'N/A';
        };

        // Helper function to get region name for a request
        const getRegionName = async (request: Request): Promise<string> => {
            const branchData = await getBranchData(request);

            if (branchData) {
                if (branchData.locationName) {
                    return branchData.locationName;
                } else if (branchData.locationId) {
                    const locationData = await fetchLocationDetails(branchData.locationId);
                    if (locationData && locationData.name) {
                        return locationData.name;
                    }
                }
            }
            return 'N/A';
        };

        // Fetch branch and region data for all requests
        const worksheetDataPromises = filteredRequests.map(async (request) => {
            const [branchName, regionName] = await Promise.all([
                getBranchName(request),
                getRegionName(request)
            ]);

            // Get time fields - check for cctvTimeFrom/cctvTimeTo first
            const timeFrom = (request as any).cctvTimeFrom && (request as any).cctvTimeFrom.trim() !== ''
                ? (request as any).cctvTimeFrom
                : (request.timeFrom && request.timeFrom.trim() !== '' ? request.timeFrom : 'N/A');
            const timeTo = (request as any).cctvTimeTo && (request as any).cctvTimeTo.trim() !== ''
                ? (request as any).cctvTimeTo
                : (request.timeTo && request.timeTo.trim() !== '' ? request.timeTo : 'N/A');

            return {
                'Request ID': request.requestId || 'N/A',
                'Request Type': request.requestType || 'N/A',
                'Branch Code': request.branchCode || 'N/A',
                'Branch Name': branchName,
                'Region': regionName,
                'Company': request.company || 'N/A',
                'Requester Name': request.requesterName || 'N/A',
                'Requester Contact': request.requesterContact || 'N/A',
                'Required By': request.requiredBy || 'N/A',
                'Date From': formatDate(getDateFields(request).dateFrom),
                'Date To': formatDate(getDateFields(request).dateTo),
                'Time From': timeFrom,
                'Time To': timeTo,
                'Number of Guards': request.numberOfGuards || 'N/A',
                'Hardware Name': request.hardwareName || 'N/A',
                'Purpose': (request as any).purposeOfFootage || request.purpose || 'N/A',
                'Status': request.status || 'N/A',
                'Created At': formatDateTime(request.createdAt),
                'Updated At': request.updatedAt ? formatDateTime(request.updatedAt) : 'N/A',
            };
        });

        const worksheetData = await Promise.all(worksheetDataPromises);

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Requests');

        const fileName = `Request_Reports_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Export to PDF
    const exportToPDF = () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF();
        let yPosition = 20;

        // Professional Header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('ADVANCE GUARD SECURITY SERVICES', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(16);
        doc.setTextColor(52, 73, 94);
        doc.text('REQUEST REPORTS', 20, yPosition);
        yPosition += 15;

        // Report Information
        doc.setFillColor(248, 249, 250);
        doc.rect(15, yPosition - 5, 180, 35, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(15, yPosition - 5, 180, 35, 'S');

        doc.setFontSize(10);
        doc.setTextColor(52, 73, 94);
        doc.setFont('helvetica', 'bold');
        doc.text('REPORT INFORMATION', 20, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${dateFilter === 'all' ? 'All Time' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Total Requests: ${filteredRequests.length}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPosition);
        yPosition += 15;

        // Prepare table data
        const tableData = filteredRequests.map(request => {
            const dates = getDateFields(request);
            return [
                request.requestId || 'N/A',
                request.requestType || 'N/A',
                request.branchCode || 'N/A',
                request.requesterName || 'N/A',
                formatDate(dates.dateFrom),
                formatDate(dates.dateTo),
                request.status || 'N/A',
                formatDate(request.createdAt),
            ];
        });

        // Add table using autoTable
        autoTable(doc, {
            head: [['Request ID', 'Type', 'Branch Code', 'Requester', 'Date From', 'Date To', 'Status', 'Created']],
            body: tableData,
            startY: yPosition,
            styles: {
                fontSize: 7,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250],
            },
            margin: { top: yPosition, left: 15, right: 15 },
        });

        const fileName = `Request_Reports_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const columns: Column<Request>[] = [
        {
            header: 'Request ID',
            accessor: 'requestId',
            className: 'whitespace-nowrap font-medium text-gray-900 dark:text-gray-100',
        },
        {
            header: 'Type',
            render: (request: Request) => getRequestTypeBadge(request.requestType),
        },
        {
            header: 'Branch Code',
            accessor: 'branchCode',
        },
        {
            header: 'Requester',
            accessor: 'requesterName',
        },
        {
            header: 'Required By',
            accessor: 'requiredBy',
        },
        {
            header: 'Date From',
            render: (request: Request) => formatDate(getDateFields(request).dateFrom),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Date To',
            render: (request: Request) => formatDate(getDateFields(request).dateTo),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Status',
            render: (request: Request) => getStatusBadge(request.status),
        },
        {
            header: 'Created',
            render: (request: Request) => formatDate(request.createdAt),
            className: 'text-gray-600 dark:text-gray-400',
        },
        {
            header: 'Action',
            render: (request: Request) => (
                <Button
                    variant="secondary"
                    className="!px-3 !py-1.5 text-xs"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(request);
                    }}
                >
                    <Icon icon={FiEye} className="mr-1 h-3 w-3" />
                    View
                </Button>
            ),
            align: 'right',
        },
    ];

    const headerActions = (
        <div className="flex gap-3">
            {canCreateRequests && (
                <Button variant="primary" onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiPlus} className="h-4 w-4" />
                    Add Request Form
                </Button>
            )}
            {!canCreateRequests && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    <Icon icon={FiAlertCircle} className="h-4 w-4" />
                    View Only - Only Incharge MIS can create requests
                </div>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
                <Button
                    variant="secondary"
                    onClick={resetFilters}
                    className="flex items-center gap-2"
                >
                    <Icon icon={FiRotateCcw} className="h-4 w-4" />
                    Reset Filters
                </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Search Requests
                    </label>
                    <TextField
                        type="text"
                        placeholder="Search by request ID, branch code, or requester..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        prefix={<Icon icon={FiSearch} />}
                    />
                </div>
                {/* Request Type Filter - Show for Incharge MIS and PSO users */}
                {(!isIncharge || isInchargeMIS) && (
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Filter by Request Type
                        </label>
                        <select
                            value={requestTypeFilter}
                            onChange={(e) => setRequestTypeFilter(e.target.value as 'All' | 'CCTV' | 'Guard' | 'BAMS')}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Types</option>
                            <option value="CCTV" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">CCTV</option>
                            <option value="Guard" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Guard</option>
                            <option value="BAMS" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">BAMS</option>
                        </select>
                    </div>
                )}
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Filter by Date Period
                    </label>
                    <select
                        value={dateFilter}
                        onChange={(e) => {
                            setDateFilter(e.target.value as 'all' | 'daily' | 'weekly' | 'monthly');
                            // Clear date range filters when period filter is used
                            if (e.target.value !== 'all') {
                                setDateFromFilter('');
                                setDateToFilter('');
                            }
                        }}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    >
                        <option value="all" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                        <option value="daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                        <option value="weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                        <option value="monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Date From
                    </label>
                    <input
                        type="date"
                        value={dateFromFilter}
                        onChange={(e) => {
                            setDateFromFilter(e.target.value);
                            // Clear period filter when date range is used
                            if (e.target.value || dateToFilter) {
                                setDateFilter('all');
                            }
                        }}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Date To
                    </label>
                    <input
                        type="date"
                        value={dateToFilter}
                        onChange={(e) => {
                            setDateToFilter(e.target.value);
                            // Clear period filter when date range is used
                            if (dateFromFilter || e.target.value) {
                                setDateFilter('all');
                            }
                        }}
                        min={dateFromFilter || undefined}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Filter by Status
                    </label>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                    >
                        <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Statuses</option>
                        {statuses.map((status) => (
                            <option key={status} value={status} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{status}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="p-6">
                <TableComp
                    title="View Requests"
                    description="Manage and track request forms"
                    data={paginatedRequests}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage="No requests found matching your criteria."
                    onRowClick={handleViewDetails}
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage,
                    }}
                />
            </div>

            {/* Request Details Modal */}
            {showDetailsModal && selectedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRequest(null);
                    }}
                    title={`Request Details - ${selectedRequest.requestId}`}
                    size="lg"
                >
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestId || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Type</label>
                                    <p className="mt-1">{getRequestTypeBadge(selectedRequest.requestType)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchCode || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{displayBranchName}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Company</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.company || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Region</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{displayRegionName}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterName || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Contact</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterContact || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Required By</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requiredBy || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    <p className="mt-1">{getStatusBadge(selectedRequest.status)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date From</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(getDateFields(selectedRequest).dateFrom)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date To</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(getDateFields(selectedRequest).dateTo)}</p>
                                </div>
                                {selectedRequest.requestType !== 'Guard' && (
                                    <>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Time From</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const cctvTimeFrom = (selectedRequest as any).cctvTimeFrom;
                                                    const timeFrom = selectedRequest.timeFrom;
                                                    if (cctvTimeFrom && cctvTimeFrom.trim() !== '') {
                                                        return cctvTimeFrom;
                                                    }
                                                    if (timeFrom && timeFrom.trim() !== '') {
                                                        return timeFrom;
                                                    }
                                                    return 'N/A';
                                                })()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Time To</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                {(() => {
                                                    const cctvTimeTo = (selectedRequest as any).cctvTimeTo;
                                                    const timeTo = selectedRequest.timeTo;
                                                    if (cctvTimeTo && cctvTimeTo.trim() !== '') {
                                                        return cctvTimeTo;
                                                    }
                                                    if (timeTo && timeTo.trim() !== '') {
                                                        return timeTo;
                                                    }
                                                    return 'N/A';
                                                })()}
                                            </p>
                                        </div>
                                    </>
                                )}
                                {selectedRequest.requestType === 'Guard' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Number of Guards</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.numberOfGuards || 'N/A'}</p>
                                    </div>
                                )}
                                {selectedRequest.requestType === 'BAMS' && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Hardware Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.hardwareName || 'N/A'}</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Purpose</label>
                                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {(selectedRequest as any).purposeOfFootage || selectedRequest.purpose || 'No purpose specified'}
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.createdAt)}</p>
                                </div>
                                {selectedRequest.updatedAt && (
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Updated At</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.updatedAt)}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer Actions */}
                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button variant="secondary" onClick={() => {
                                setShowDetailsModal(false);
                                setSelectedRequest(null);
                            }}>
                                Close
                            </Button>

                            {/* Regular PSO: No action buttons */}
                            {psoDesignation === 'PSO' && (
                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <Icon icon={FiAlertCircle} className="mr-1 h-4 w-4" />
                                    View only - No actions available
                                </div>
                            )}

                            {/* Regional PSO: Approve/Reject buttons for pending requests */}
                            {psoDesignation === 'Regional PSO' && selectedRequest?.status === 'pending' && (
                                <>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleUpdateStatus(selectedRequest, 'approved')}
                                        disabled={updating}
                                    >
                                        {updating ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                Approving...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                Approve
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleUpdateStatus(selectedRequest, 'rejected')}
                                        disabled={updating}
                                        className="!bg-red-600 hover:!bg-red-700"
                                    >
                                        {updating ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                Rejecting...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                                Reject
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleForwardToHead(selectedRequest)}
                                        disabled={updating}
                                    >
                                        {updating ? (
                                            <>
                                                <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                Forwarding...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon={FiClock} className="mr-1 h-4 w-4" />
                                                Forward to Head PSO
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}

                            {/* Regional PSO: Hide buttons if already forwarded */}
                            {psoDesignation === 'Regional PSO' && selectedRequest?.status === 'forwarded_to_head' && (
                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                    Request already forwarded to Head PSO
                                </div>
                            )}

                            {/* Regional PSO: Show status message for approved/rejected requests */}
                            {psoDesignation === 'Regional PSO' &&
                                selectedRequest?.status !== 'pending' &&
                                selectedRequest?.status !== 'forwarded_to_head' && (
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                        Request status: {selectedRequest?.status}
                                    </div>
                                )}

                            {/* Head PSO: Approve/Reject buttons */}
                            {psoDesignation === 'Head PSO' && (
                                <>
                                    {(selectedRequest?.status === 'pending' || selectedRequest?.status === 'forwarded_to_head') && (
                                        <>
                                            <Button
                                                variant="primary"
                                                onClick={() => handleUpdateStatus(selectedRequest, 'approved')}
                                                disabled={updating}
                                            >
                                                {updating ? (
                                                    <>
                                                        <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                        Approving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                        Approve
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                onClick={() => handleUpdateStatus(selectedRequest, 'rejected')}
                                                disabled={updating}
                                                className="!bg-red-600 hover:!bg-red-700"
                                            >
                                                {updating ? (
                                                    <>
                                                        <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                        Rejecting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Icon icon={FiX} className="mr-1 h-4 w-4" />
                                                        Reject
                                                    </>
                                                )}
                                            </Button>
                                        </>
                                    )}
                                    {selectedRequest?.status === 'approved' && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleUpdateStatus(selectedRequest, 'in_progress')}
                                            disabled={updating}
                                        >
                                            {updating ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Starting...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiClock} className="mr-1 h-4 w-4" />
                                                    Start Processing
                                                </>
                                            )}
                                        </Button>
                                    )}
                                    {selectedRequest?.status === 'in_progress' && (
                                        <Button
                                            variant="primary"
                                            onClick={() => handleUpdateStatus(selectedRequest, 'completed')}
                                            disabled={updating}
                                        >
                                            {updating ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Completing...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiCheck} className="mr-1 h-4 w-4" />
                                                    Mark Complete
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Request Form Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="Request Form"
                    size="xl"
                >
                    <div className="p-6">
                        {formData.requestId && (
                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                    <strong>Request ID:</strong> {formData.requestId}
                                </p>
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Request Type */}
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Request Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.requestType}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            requestType: e.target.value,
                                            company: '',
                                            selectedCompanyId: '',
                                        });
                                    }}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                >
                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Request Type</option>
                                    {requestTypeOptions.map((type) => (
                                        <option key={type} value={type} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{type}</option>
                                    ))}
                                </select>
                            </div>

                            {formData.requestType && (
                                <>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        {/* Branch Code */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch Code <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.branchCode}
                                                placeholder="Click to select branch"
                                                readOnly
                                                onClick={() => setShowBranchModal(true)}
                                                className="cursor-pointer"
                                            />
                                        </div>

                                        {/* Branch Name */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch Name
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.branchName}
                                                placeholder="Auto-filled from branch selection"
                                                readOnly
                                            />
                                        </div>

                                        {/* Required By - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Required By <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={formData.requiredBy}
                                                    onChange={(e) => setFormData({ ...formData, requiredBy: e.target.value })}
                                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                >
                                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Required By</option>
                                                    {requiredByOptions.map((option) => (
                                                        <option key={option} value={option} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{option}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Company */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Company <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.company}
                                                placeholder={`Click to select ${formData.requestType} company`}
                                                readOnly
                                                onClick={() => {
                                                    loadAvailableCompanies();
                                                    setShowCompanyModal(true);
                                                }}
                                                className="cursor-pointer"
                                            />
                                        </div>

                                        {/* Requester Name - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Requester Name <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="text"
                                                    value={formData.requesterName}
                                                    onChange={(e) => setFormData({ ...formData, requesterName: e.target.value })}
                                                    placeholder="Enter requester name"
                                                />
                                            </div>
                                        )}

                                        {/* Requester Contact - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Requester Contact <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="text"
                                                    value={formData.requesterContact}
                                                    onChange={(e) => setFormData({ ...formData, requesterContact: e.target.value })}
                                                    placeholder="Enter contact number"
                                                />
                                            </div>
                                        )}

                                        {/* Date From */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Date From <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.cctvDateFrom}
                                                onChange={(e) => setFormData({ ...formData, cctvDateFrom: e.target.value })}
                                            />
                                        </div>

                                        {/* Date To */}
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Date To <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.cctvDateTo}
                                                onChange={(e) => setFormData({ ...formData, cctvDateTo: e.target.value })}
                                            />
                                        </div>

                                        {/* Time From - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Time From <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="time"
                                                    value={formData.timeFrom}
                                                    onChange={(e) => setFormData({ ...formData, timeFrom: e.target.value })}
                                                />
                                            </div>
                                        )}

                                        {/* Time To - Hidden for Guard */}
                                        {true && (
                                            <div>
                                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                    Time To <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    type="time"
                                                    value={formData.timeTo}
                                                    onChange={(e) => setFormData({ ...formData, timeTo: e.target.value })}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Additional Fields based on Request Type */}
                                    {formData.requestType === 'Guard' && (
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Number of Guards <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.numberOfGuards}
                                                onChange={(e) => setFormData({ ...formData, numberOfGuards: e.target.value })}
                                                placeholder="Enter number of guards"
                                            />
                                        </div>
                                    )}


                                    {/* Purpose */}
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Purpose <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.purpose}
                                            onChange={(e) => setFormData({ ...formData, purpose: e.target.value, purposeOther: '' })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                        >
                                            <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Purpose</option>
                                            {(formData.requestType === 'Guard' ? purposeOptionsGuard : purposeOptionsCCTV).map((purpose) => (
                                                <option key={purpose} value={purpose} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">{purpose}</option>
                                            ))}
                                        </select>
                                        {formData.purpose === 'Other' && (
                                            <div className="mt-3">
                                                <TextField
                                                    type="text"
                                                    value={formData.purposeOther}
                                                    onChange={(e) => setFormData({ ...formData, purposeOther: e.target.value })}
                                                    placeholder={formData.requestType === 'Guard' ? 'Please specify the reason...' : 'Please specify the purpose...'}
                                                    className="mt-1"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setShowAddModal(false);
                                                resetForm();
                                            }}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSaveRequest}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                                                    Submitting...
                                                </>
                                            ) : (
                                                'Submit Request'
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Branch Selection Modal */}
            {showBranchModal && (
                <Modal
                    isOpen={showBranchModal}
                    onClose={() => setShowBranchModal(false)}
                    title="Select Branch"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <TextField
                                type="text"
                                placeholder="Search branch by code or address"
                                value={branchSearch}
                                onChange={(e) => setBranchSearch(e.target.value)}
                                prefix={<Icon icon={FiSearch} />}
                            />
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {filteredBranches.length === 0 ? (
                                <p className="py-4 text-center text-gray-500 dark:text-gray-400">No branches found</p>
                            ) : (
                                <div className="space-y-2">
                                    {filteredBranches.map((branch) => (
                                        <button
                                            key={branch.id}
                                            type="button"
                                            onClick={() => handleBranchSelection(branch)}
                                            className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                                        >
                                            <div className="font-semibold text-gray-900 dark:text-gray-100">{branch.branchCode}</div>
                                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                {branch.address || 'No address available'}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Company Selection Modal */}
            {showCompanyModal && (
                <Modal
                    isOpen={showCompanyModal}
                    onClose={() => setShowCompanyModal(false)}
                    title={`Select ${formData.requestType} Company`}
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <TextField
                                type="text"
                                placeholder="Search company by name or ID"
                                value={companySearch}
                                onChange={(e) => setCompanySearch(e.target.value)}
                                prefix={<Icon icon={FiSearch} />}
                            />
                        </div>
                        <div className="mb-3">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Found {availableCompanies.length} companies for {formData.requestType} requests
                            </p>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {availableCompanies.length === 0 ? (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                    <p className="text-sm text-blue-900 dark:text-blue-100">
                                        No {formData.requestType} companies found. Please add companies first.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {availableCompanies
                                        .filter(company =>
                                            company.companyName?.toLowerCase().includes(companySearch.toLowerCase()) ||
                                            company.companyId?.toLowerCase().includes(companySearch.toLowerCase())
                                        )
                                        .map((company) => (
                                            <button
                                                key={company.id}
                                                type="button"
                                                onClick={() => handleCompanySelection(company)}
                                                className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                                            >
                                                <div className="font-semibold text-gray-900 dark:text-gray-100">{company.companyName}</div>
                                                <div className="mt-1 space-y-1 text-sm text-gray-500 dark:text-gray-400">
                                                    <div>ID: {company.companyId}</div>
                                                    <div>Type: {company.companyType}</div>
                                                    {company.companyAddress && <div>Address: {company.companyAddress}</div>}
                                                    {company.email && <div>Email: {company.email}</div>}
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toastMessage}
                            type={toastType}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

